import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import NumberFlow from "@number-flow/react";
import {
  InfoIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  GlobeIcon,
  ChevronsUpDownIcon,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
} from "@/components/ui/switch-card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@clerk/react";
import { useCampaignStore } from "@/stores";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameFooter,
  FrameAction,
} from "@/components/ui/frame";
import { Separator } from "@/components/ui/separator";
import { isEveCampaign } from "@/lib/eve";
import { AmyMailboxFrame } from "./AmyMailboxFrame";

const TIMEZONE_OPTIONS = [
  { value: "America/Los_Angeles", label: "Pacific Time - PT" },
  { value: "America/Denver", label: "Mountain Time - MT" },
  { value: "America/Chicago", label: "Central Time - CT" },
  { value: "America/New_York", label: "Eastern Time - ET" },
  { value: "America/Toronto", label: "Toronto - ET" },
  { value: "America/Sao_Paulo", label: "São Paulo -BRT" },
  { value: "Europe/London", label: "London - GMT/BST" },
  { value: "Europe/Paris", label: "Paris - CET" },
  { value: "Europe/Berlin", label: "Berlin - CET" },
  { value: "Europe/Istanbul", label: "Istanbul - TRT" },
  { value: "Asia/Dubai", label: "Dubai - GST" },
  { value: "Asia/Kolkata", label: "India - IST" },
  { value: "Asia/Singapore", label: "Singapore - SGT" },
  { value: "Asia/Shanghai", label: "Shanghai - CST" },
  { value: "Asia/Tokyo", label: "Tokyo - JST" },
  { value: "Australia/Sydney", label: "Sydney - AEST" },
  { value: "Pacific/Auckland", label: "Auckland - NZST" },
];

const HOUR_MARKERS = [
  { hour: 0, label: "12 am" },
  { hour: 3, label: "3 am" },
  { hour: 6, label: "6 am" },
  { hour: 9, label: "9 am" },
  { hour: 12, label: "12 pm" },
  { hour: 15, label: "3 pm" },
  { hour: 18, label: "6 pm" },
  { hour: 21, label: "9 pm" },
  { hour: 23, label: "11 pm" },
];

interface EmailLimits {
  outreach_hourly_limit: number;
  reply_hourly_limit: number;
  follow_up_hourly_limit: number;
  follow_up_reply_hourly_limit: number;
  max_follow_ups: number;
  max_follow_ups_for_replied_threads: number;
  follow_up_wait_time_days: number;
}

const DEFAULT_SCHEDULE = {
  timezone: "America/New_York",
  start_hour: 12,
  end_hour: 17,
};

const DEFAULT_EMAIL_LIMITS: EmailLimits = {
  outreach_hourly_limit: 3,
  reply_hourly_limit: 8,
  follow_up_hourly_limit: 2,
  follow_up_reply_hourly_limit: 2,
  max_follow_ups: 2,
  max_follow_ups_for_replied_threads: 2,
  follow_up_wait_time_days: 2,
};

const DEFAULT_FOLLOW_UP_WAIT_SECONDS =
  DEFAULT_EMAIL_LIMITS.follow_up_wait_time_days * 86400;

const scheduleFromCampaign = (
  s?: {
    timezone?: string;
    start_hour?: number;
    end_hour?: number;
  } | null,
) => ({
  timezone: s?.timezone ?? DEFAULT_SCHEDULE.timezone,
  start_hour: s?.start_hour ?? DEFAULT_SCHEDULE.start_hour,
  end_hour: s?.end_hour ?? DEFAULT_SCHEDULE.end_hour,
});

const emailLimitsFromCampaign = (
  l?:
    | (Partial<Omit<EmailLimits, "follow_up_wait_time_days">> & {
        follow_up_wait_time?: number;
      })
    | null,
): EmailLimits => ({
  outreach_hourly_limit:
    l?.outreach_hourly_limit ?? DEFAULT_EMAIL_LIMITS.outreach_hourly_limit,
  reply_hourly_limit:
    l?.reply_hourly_limit ?? DEFAULT_EMAIL_LIMITS.reply_hourly_limit,
  follow_up_hourly_limit:
    l?.follow_up_hourly_limit ?? DEFAULT_EMAIL_LIMITS.follow_up_hourly_limit,
  follow_up_reply_hourly_limit:
    l?.follow_up_reply_hourly_limit ??
    DEFAULT_EMAIL_LIMITS.follow_up_reply_hourly_limit,
  max_follow_ups: l?.max_follow_ups ?? DEFAULT_EMAIL_LIMITS.max_follow_ups,
  max_follow_ups_for_replied_threads:
    l?.max_follow_ups_for_replied_threads ??
    DEFAULT_EMAIL_LIMITS.max_follow_ups_for_replied_threads,
  follow_up_wait_time_days: l?.follow_up_wait_time
    ? l.follow_up_wait_time / 86400
    : DEFAULT_EMAIL_LIMITS.follow_up_wait_time_days,
});

const emailLimitFields: {
  label: string;
  key: keyof EmailLimits;
  min: number;
  max: number;
  default: number;
  step: number;
  helper: string;
  is_daily: boolean;
}[] = [
  {
    label: "Outreach Hourly Limit",
    key: "outreach_hourly_limit",
    min: 0,
    max: 5,
    default: 3,
    step: 1,
    helper: "Max initial outreach emails per hour",
    is_daily: true,
  },
  {
    label: "Reply Hourly Limit",
    key: "reply_hourly_limit",
    min: 0,
    max: 16,
    default: 8,
    step: 1,
    helper: "Max replies to creators per hour",
    is_daily: true,
  },
  {
    label: "Follow-up Hourly Limit",
    key: "follow_up_hourly_limit",
    min: 0,
    max: 4,
    default: 2,
    step: 1,
    helper: "Max follow-ups to non-responsive creators per hour",
    is_daily: true,
  },
  {
    label: "Follow-up Reply Hourly Limit",
    key: "follow_up_reply_hourly_limit",
    min: 0,
    max: 4,
    default: 2,
    step: 1,
    helper: "Max follow-ups in ongoing conversations per hour",
    is_daily: true,
  },
  {
    label: "Max Follow-ups",
    key: "max_follow_ups",
    min: 0,
    max: 3,
    default: 2,
    step: 1,
    helper: "Max number of follow-ups before stopping",
    is_daily: false,
  },
  {
    label: "Max Follow-ups (After Reply)",
    key: "max_follow_ups_for_replied_threads",
    min: 0,
    max: 3,
    default: 2,
    step: 1,
    helper: "Max consecutive Amy emails in active conversations",
    is_daily: false,
  },
  {
    label: "Follow-up Wait Time (Days)",
    key: "follow_up_wait_time_days",
    min: 1,
    max: 7,
    default: 2,
    step: 0.5,
    helper: "Days to wait before sending follow-up emails",
    is_daily: false,
  },
];

/**
 * Settings → Email (the sending mechanics; split from "Email & Messaging"
 * on 2026-10-22): autopilot, schedule, the per-mailbox email limits and
 * Amy's mailboxes (eve campaigns). Messaging mode, subject, the Initial
 * Outreach Template and CC emails live in MessagingSection.
 */
export function EmailSection() {
  const {
    campaign: c,
    campaignId,
    updateCampaignField,
    updatingCampaignField,
    updateCampaignAutopilot,
  } = useCampaignStore();
  const { getToken } = useAuth();

  const [tzOpen, setTzOpen] = useState(false);

  const [schedule, setSchedule] = useState(scheduleFromCampaign(c?.schedule));

  // Eve-run campaigns: every limit is per mailbox — the daily math multiplies
  // by the mailboxes switched on (0 = legacy campaign or none attached yet).
  const mailboxesOn = isEveCampaign(c) ? (c?.eve_mailboxes ?? []).filter((m) => !m.paused).length : 0;
  const activeHours = useMemo(
    () => Math.max(schedule.end_hour - schedule.start_hour + 1, 0),
    [schedule.start_hour, schedule.end_hour],
  );

  const [emailLimits, setEmailLimits] = useState<EmailLimits>(
    emailLimitsFromCampaign(c?.email_limits),
  );

  const isScheduleDirty = useMemo(() => {
    if (!c) return false;
    const server = scheduleFromCampaign(c.schedule);
    return (
      schedule.timezone !== server.timezone ||
      schedule.start_hour !== server.start_hour ||
      schedule.end_hour !== server.end_hour
    );
  }, [c, schedule]);

  const isEmailLimitsDirty = useMemo(() => {
    if (!c) return false;
    const server = c.email_limits ?? {};
    const localWaitSeconds = emailLimits.follow_up_wait_time_days * 86400;
    return (
      emailLimits.outreach_hourly_limit !==
        (server.outreach_hourly_limit ??
          DEFAULT_EMAIL_LIMITS.outreach_hourly_limit) ||
      emailLimits.reply_hourly_limit !==
        (server.reply_hourly_limit ??
          DEFAULT_EMAIL_LIMITS.reply_hourly_limit) ||
      emailLimits.follow_up_hourly_limit !==
        (server.follow_up_hourly_limit ??
          DEFAULT_EMAIL_LIMITS.follow_up_hourly_limit) ||
      emailLimits.follow_up_reply_hourly_limit !==
        (server.follow_up_reply_hourly_limit ??
          DEFAULT_EMAIL_LIMITS.follow_up_reply_hourly_limit) ||
      emailLimits.max_follow_ups !==
        (server.max_follow_ups ?? DEFAULT_EMAIL_LIMITS.max_follow_ups) ||
      emailLimits.max_follow_ups_for_replied_threads !==
        (server.max_follow_ups_for_replied_threads ??
          DEFAULT_EMAIL_LIMITS.max_follow_ups_for_replied_threads) ||
      localWaitSeconds !==
        (server.follow_up_wait_time ?? DEFAULT_FOLLOW_UP_WAIT_SECONDS)
    );
  }, [c, emailLimits]);

  useEffect(() => {
    if (c) {
      setEmailLimits(emailLimitsFromCampaign(c.email_limits));
      setSchedule(scheduleFromCampaign(c.schedule));
    }
  }, [c]);

  const handleUpdateCampaignAutopilot = async (newValue: boolean) => {
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");
    await updateCampaignAutopilot(campaignId, newValue, token);
  };

  const handleSaveEmailLimits = async () => {
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");

    const waitTimeSeconds = emailLimits.follow_up_wait_time_days * 60 * 60 * 24;

    const emailLimitsToSave = {
      outreach_hourly_limit: emailLimits.outreach_hourly_limit,
      reply_hourly_limit: emailLimits.reply_hourly_limit,
      follow_up_hourly_limit: emailLimits.follow_up_hourly_limit,
      follow_up_reply_hourly_limit: emailLimits.follow_up_reply_hourly_limit,
      max_follow_ups: emailLimits.max_follow_ups,
      max_follow_ups_for_replied_threads:
        emailLimits.max_follow_ups_for_replied_threads,
      follow_up_wait_time: waitTimeSeconds,
    };

    await updateCampaignField(
      "email_limits",
      JSON.stringify(emailLimitsToSave),
      campaignId,
      token,
    );
  };

  const handleSaveSchedule = async () => {
    if (schedule.start_hour >= schedule.end_hour) return;
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");
    await updateCampaignField(
      "schedule",
      JSON.stringify(schedule),
      campaignId,
      token,
    );
  };

  const handleResetSchedule = () => setSchedule({ ...DEFAULT_SCHEDULE });

  const handleCancelSchedule = () =>
    setSchedule(scheduleFromCampaign(c?.schedule));

  const handleResetEmailLimits = () =>
    setEmailLimits({ ...DEFAULT_EMAIL_LIMITS });

  const handleCancelEmailLimits = () =>
    setEmailLimits(emailLimitsFromCampaign(c?.email_limits));

  return (
    <div className="space-y-6">
      {/* Eve-run campaigns have no autopilot here: the legacy flag must STAY off
          (the legacy cron would email into Amy's conversations — the backend
          409s this route as a backstop); Amy's own switch is on Pipeline › Overview. */}
      {!isEveCampaign(c) && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Autopilot</FrameTitle>
          </FrameHeader>
          <SwitchCard disabled={updatingCampaignField}>
            <SwitchCardSwitch
              id="campaign_autopilot"
              checked={c?.autopilot ?? true}
              onCheckedChange={(value: boolean) =>
                handleUpdateCampaignAutopilot(value)
              }
            />
            <SwitchCardContent>
              <SwitchCardLabel htmlFor="campaign_autopilot">
                {!updatingCampaignField && c?.autopilot && <PlayCircleIcon />}
                {!updatingCampaignField && !c?.autopilot && <PauseCircleIcon />}
                {updatingCampaignField && <Spinner />}
                Amy on Autopilot
                <span className="text-muted-foreground text-xs">
                  {" "}
                  - Only for this campaign
                </span>
              </SwitchCardLabel>
            </SwitchCardContent>
          </SwitchCard>
        </Frame>
      )}

      <Frame>
        <FrameHeader>
          <FrameTitle>Schedule</FrameTitle>
          <FrameAction>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="muted-ghost" size="icon-sm" aria-label="How the schedule adds up">
                  <InfoIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 border-0 bg-transparent p-0 shadow-lg" align="end">
                <Frame>
                  <FrameHeader>
                    <FrameTitle className="text-sm">Quick recap</FrameTitle>
                  </FrameHeader>
                  <FramePanel className="p-2.5 text-xs">
                    Amy runs once per hour, on the hour, within your schedule.
                  </FramePanel>
                  <FramePanel className="flex flex-col gap-1.5 p-2.5 text-xs">
                    <div className="flex justify-between">
                      <span>Your schedule</span>
                      <span className="font-mono">
                        {schedule.start_hour}:00 to {schedule.end_hour}:00
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Runs per day</span>
                      <span className="font-mono">{activeHours}</span>
                    </div>
                    <Separator className="my-1" />
                    <div className="flex justify-between">
                      <span>Outreach cap</span>
                      <span className="font-medium font-mono">
                        {activeHours} × {emailLimits["outreach_hourly_limit"]}
                        {mailboxesOn > 1 ? ` × ${mailboxesOn}` : ""} ={" "}
                        {activeHours * emailLimits["outreach_hourly_limit"] * Math.max(1, mailboxesOn)}/day
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Reply cap</span>
                      <span className="font-medium font-mono">
                        {activeHours} × {emailLimits["reply_hourly_limit"]}
                        {mailboxesOn > 1 ? ` × ${mailboxesOn}` : ""} ={" "}
                        {activeHours * emailLimits["reply_hourly_limit"] * Math.max(1, mailboxesOn)}/day
                      </span>
                    </div>
                    <span className="pt-2 text-muted-foreground">. . . and so on for follow-ups, etc.</span>
                  </FramePanel>
                </Frame>
              </PopoverContent>
            </Popover>
          </FrameAction>
        </FrameHeader>
        <FramePanel>
          <div className="flex-1 flex-col items-center space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Popover open={tzOpen} onOpenChange={setTzOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={tzOpen}
                    className="w-92 shrink-0"
                  >
                    <GlobeIcon className="size-3.5 text-muted-foreground" />
                    <span className="mr-auto">
                      {TIMEZONE_OPTIONS.find(
                        (tz) => tz.value === schedule.timezone,
                      )?.label ?? schedule.timezone}
                    </span>
                    <span className="text-xs text-muted-foreground gap-2 font-normal text-end">
                      {TIMEZONE_OPTIONS.find(
                        (tz) => tz.value === schedule.timezone,
                      )?.value ?? schedule.timezone}{" "}
                      -{" "}
                      {new Date().toLocaleTimeString("en-US", {
                        timeZone:
                          TIMEZONE_OPTIONS.find(
                            (tz) => tz.value === schedule.timezone,
                          )?.value ?? schedule.timezone,
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </span>
                    <ChevronsUpDownIcon className="size-3.5 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-92">
                  <Command className="max-h-78 w-full">
                    <CommandInput
                      className="sm:text-xs"
                      placeholder="Search timezone..."
                    />
                    <CommandList>
                      <CommandEmpty>No timezone found.</CommandEmpty>
                      <CommandGroup>
                        {TIMEZONE_OPTIONS.map((tz) => (
                          <CommandItem
                            key={tz.value}
                            value={`${tz.label} ${tz.value}`}
                            className="text-nowrap"
                            onSelect={() => {
                              setSchedule({ ...schedule, timezone: tz.value });
                              setTzOpen(false);
                            }}
                          >
                            <span className="text-xs mr-auto pr-5 font-medium">
                              {tz.label}
                            </span>
                            <span className="text-xs text-muted-foreground gap-2">
                              {tz.value} -{" "}
                              {new Date().toLocaleTimeString("en-US", {
                                timeZone: tz.value,
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <span className="font-mono text-sm flex items-center gap-1 ml-auto bg-sidebar px-1.5 py-0.5 rounded-sm">
                <NumberFlow
                  value={schedule.start_hour !== 0 ? schedule.start_hour : 12}
                  suffix=":00"
                />
                -
                <NumberFlow
                  value={schedule.end_hour !== 0 ? schedule.end_hour : 12}
                  suffix=":00"
                />
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                <NumberFlow value={activeHours} suffix=" times" prefix="is " />{" "}
                daily
              </span>
            </div>
            <div className="flex flex-col p-3 justify-center items-center">
              <Slider
                value={[schedule.start_hour, schedule.end_hour]}
                min={0}
                max={23}
                step={1}
                onValueChange={(vals) =>
                  setSchedule({
                    ...schedule,
                    start_hour: vals[0],
                    end_hour: vals[1],
                  })
                }
              />
              <div className="relative w-full self-center mt-2">
                {HOUR_MARKERS.map((m) => (
                  <span
                    key={m.hour}
                    className="absolute -translate-x-1/2 text-[10px] text-muted-foreground text-nowrap"
                    style={{ left: `${(m.hour / 23) * 100}%` }}
                  >
                    {m.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </FramePanel>
        <FrameFooter className="flex justify-end gap-2">
          {!isScheduleDirty && (
            <Button
              size="sm"
              variant="secondary"
              disabled={updatingCampaignField}
              onClick={() => handleResetSchedule()}
            >
              Reset to Default
            </Button>
          )}
          {isScheduleDirty && (
            <>
              <Button
                size="sm"
                variant="secondary"
                className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
                disabled={updatingCampaignField}
                onClick={() => handleCancelSchedule()}
                aria-label="Discard schedule changes"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
                disabled={updatingCampaignField}
                onClick={() => handleSaveSchedule()}
              >
                Save Schedule
              </Button>
            </>
          )}
        </FrameFooter>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Email Limits</FrameTitle>
          <FrameDescription>
            {`Amy runs ${activeHours} ${activeHours === 1 ? "hour" : "hours"} daily`}
            {mailboxesOn > 0
              ? `. Limits are per mailbox, per active hour — ${mailboxesOn} ${mailboxesOn === 1 ? "mailbox is" : "mailboxes are"} switched on.`
              : ". Set email limits per active hour."}
          </FrameDescription>
        </FrameHeader>
        <FramePanel>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-6">
            {emailLimitFields.map((field) => (
              <div key={field.key} className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <Tooltip delayDuration={500}>
                    <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                      <Label
                        className={`${emailLimits[field.key] === 0 ? "text-muted-foreground line-through" : ""} transition-all`}
                      >
                        {field.label}
                      </Label>
                      <InfoIcon className="size-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      {emailLimits[field.key] === 0 && (
                        <span className="text-destructive">
                          Disabled: <br />
                        </span>
                      )}
                      {field.helper}
                    </TooltipContent>
                  </Tooltip>
                  {field.is_daily && (
                    <span className="font-mono text-sm mt-auto font-medium">
                      <NumberFlow value={emailLimits[field.key]} suffix=" ×" />{" "}
                      <NumberFlow value={activeHours} />
                      {mailboxesOn > 1 && (
                        <>
                          {" "}
                          <NumberFlow value={mailboxesOn} prefix="× " />
                        </>
                      )}
                      <span className="mx-1.5">
                        <NumberFlow
                          prefix="= "
                          value={emailLimits[field.key] * activeHours * Math.max(1, mailboxesOn)}
                        />
                      </span>
                      daily
                    </span>
                  )}
                  {!field.is_daily && (
                    <span className="font-mono text-sm mt-auto font-medium">
                      <NumberFlow value={emailLimits[field.key]} />
                    </span>
                  )}
                </div>
                <Slider
                  value={[emailLimits[field.key]]}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  onValueChange={(value) =>
                    setEmailLimits({ ...emailLimits, [field.key]: value[0] })
                  }
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </FramePanel>
        <FrameFooter className="flex justify-end gap-2">
          {!isEmailLimitsDirty && (
            <Button
              size="sm"
              variant="secondary"
              disabled={updatingCampaignField}
              onClick={() => handleResetEmailLimits()}
            >
              Reset to Default
            </Button>
          )}
          {isEmailLimitsDirty && (
            <>
              <Button
                size="sm"
                variant="secondary"
                className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
                disabled={updatingCampaignField}
                onClick={() => handleCancelEmailLimits()}
                aria-label="Discard email limit changes"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
                disabled={updatingCampaignField}
                onClick={() => handleSaveEmailLimits()}
              >
                Save Email Limits
              </Button>
            </>
          )}
        </FrameFooter>
      </Frame>

      {/* Eve-run campaigns send from their own list of Composio pool
          mailboxes; the limits above apply to EACH of them. */}
      {isEveCampaign(c) && <AmyMailboxFrame />}
    </div>
  );
}
